import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { subscriptionAPI } from '../../services/api';
import paystackService from '../../services/paystack.service';
import { toast } from 'react-toastify';

const PremiumUpgradeModal = ({ onClose }) => {
  const [plans,        setPlans]    = useState([]);
  const [selected,     setSelected] = useState(null);
  const [fetchingPlans,setFetching] = useState(true);
  const [paying,       setPaying]   = useState(false);

  useEffect(() => {
    setFetching(true);
    subscriptionAPI.getPlans()
      .then(res => {
        const p = res.data.plans || [];
        setPlans(p);
        if (p.length) setSelected(p[0]);
      })
      .catch(() => {
        const fallback = [
          {
            id: 'premium_monthly',
            name: 'Premium Monthly',
            billing_cycle: 'monthly',
            price: 2000,
            currency: 'NGN',
            features: ['Book unlimited buddy sessions', 'Priority facilitator matching', 'Advanced session scheduling', 'Extended session duration', 'Personalized support plans', 'Access to premium resources'],
          },
          {
            id: 'premium_yearly',
            name: 'Premium Yearly',
            billing_cycle: 'yearly',
            price: 20000,
            currency: 'NGN',
            features: ['Everything in Monthly', 'Save ~17%', 'Early access to new features'],
          },
        ];
        setPlans(fallback);
        setSelected(fallback[0]);
        toast.warn('Could not load plan details from server – using defaults.');
      })
      .finally(() => setFetching(false));
  }, []);

  const handleUpgrade = async () => {
    if (!selected || paying) return;
    setPaying(true);

    await paystackService.processSubscription(
      {
        amount:       selected.price,
        billingCycle: selected.billing_cycle,
        planName:     selected.name,
      },
      async (response) => {
        try {
          const res = await subscriptionAPI.activate({
            payment_reference: response.reference,
            billing_cycle:     selected.billing_cycle,
          });
          toast.success('🎉 Premium activated successfully!');
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          localStorage.setItem('user', JSON.stringify({ ...currentUser, user_type: 'premium' }));
          window.dispatchEvent(new Event('authChange'));
          onClose();
        } catch (err) {
          const msg = err.response?.data?.error
            || `Activation failed. Contact support with reference: ${response.reference}`;
          toast.error(msg, { autoClose: 10000 });
        } finally {
          setPaying(false);
        }
      },
      (err) => {
        setPaying(false);
        if (err?.message !== 'Payment window closed') {
          toast.error(err?.message || 'Payment failed. Please try again.');
        }
      }
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '16px',
        maxWidth: '520px',
        width: '100%',
        border: '2px solid #FFB6C1',
        boxShadow: '0 20px 60px rgba(255, 182, 193, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative blob */}
        <div style={{
          position: 'absolute', top: '-50px', right: '-50px',
          width: '150px', height: '150px',
          background: 'linear-gradient(135deg, #FFB6C1 0%, #FF69B4 100%)',
          borderRadius: '50%', opacity: 0.1
        }} />

        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: '1rem', right: '1rem',
            background: 'none', border: 'none', color: '#999',
            fontSize: '1.5rem', cursor: 'pointer', zIndex: 1
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#FFB6C1'}
          onMouseLeave={e => e.currentTarget.style.color = '#999'}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div style={{ padding: '3rem 2rem 2rem' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '72px', height: '72px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #FFB6C1 0%, #FF69B4 100%)',
              marginBottom: '1rem', animation: 'pulse 2s infinite'
            }}>
              <FontAwesomeIcon icon={faCrown} style={{ fontSize: '2rem', color: '#fff' }} />
            </div>
            <h2 style={{ color: '#FFB6C1', fontSize: '1.8rem', marginBottom: '0.4rem', fontWeight: 700 }}>
              Upgrade to Premium
            </h2>
            <p style={{ color: '#999', fontSize: '1rem' }}>Unlock the full power of Buddy Sessions</p>
          </div>

          {fetchingPlans ? (
            <div style={{ textAlign: 'center', color: '#888', padding: '2rem 0' }}>Loading plans…</div>
          ) : (
            <>
              {/* Plan selector */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '1.25rem' }}>
                {plans.map(plan => (
                  <div
                    key={plan.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => !paying && setSelected(plan)}
                    onKeyDown={e => e.key === 'Enter' && !paying && setSelected(plan)}
                    style={{
                      flex: 1, borderRadius: '12px', padding: '14px',
                      textAlign: 'center', position: 'relative', userSelect: 'none',
                      cursor: paying ? 'not-allowed' : 'pointer',
                      border: selected?.id === plan.id ? '2px solid #FFB6C1' : '2px solid #333',
                      background: selected?.id === plan.id ? 'rgba(255,182,193,0.08)' : 'transparent',
                      transition: 'all 0.15s'
                    }}
                  >
                    {plan.billing_cycle === 'yearly' && (
                      <span style={{
                        position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
                        background: '#FFB6C1', color: '#000', fontSize: '10px',
                        fontWeight: 700, padding: '2px 10px', borderRadius: '20px', whiteSpace: 'nowrap'
                      }}>Best Value</span>
                    )}
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#ccc', marginBottom: '4px' }}>{plan.name}</div>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: '#FFB6C1' }}>
                      ₦{(plan.price || 0).toLocaleString()}
                      <span style={{ fontSize: '12px', fontWeight: 400, color: '#777' }}>
                        /{plan.billing_cycle === 'yearly' ? 'yr' : 'mo'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Features */}
              {selected && (
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(selected.features || []).map((f, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.95rem', color: '#fff' }}>
                      <FontAwesomeIcon icon={faCheck} style={{ color: '#FFB6C1', fontSize: '0.85rem', flexShrink: 0 }} />
                      {f}
                    </li>
                  ))}
                </ul>
              )}

              {/* Pay button */}
              <button
                onClick={handleUpgrade}
                disabled={paying || !selected}
                style={{
                  width: '100%', padding: '14px',
                  background: 'linear-gradient(135deg, #FFB6C1 0%, #FF69B4 100%)',
                  color: '#fff', border: 'none', borderRadius: '10px',
                  fontSize: '1rem', fontWeight: 700, cursor: (paying || !selected) ? 'not-allowed' : 'pointer',
                  opacity: (paying || !selected) ? 0.65 : 1, marginBottom: '0.75rem',
                  transition: 'opacity 0.15s'
                }}
              >
                {paying ? '⏳ Processing…' : `Pay ₦${(selected?.price || 0).toLocaleString()} & Upgrade`}
              </button>

              <p style={{ textAlign: 'center', color: '#666', fontSize: '12px', margin: 0 }}>
                🔒 Secured by Paystack · Cancel anytime
              </p>

              <button
                onClick={onClose}
                disabled={paying}
                style={{
                  display: 'block', width: '100%', marginTop: '0.75rem',
                  padding: '10px', backgroundColor: 'transparent',
                  color: '#666', border: '1px solid #333', borderRadius: '8px',
                  fontSize: '0.9rem', fontWeight: 600, cursor: paying ? 'not-allowed' : 'pointer'
                }}
              >
                Maybe Later
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default PremiumUpgradeModal;
